import { Box, Button, List, ListItem, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';
import { Certificate } from '../types';
import { getBeautifiedLocalDate, getMessageFromError } from '@/core';

import { downloadFileFromUrl } from '@/common';
import { downloadCertificateFromDws } from '../api/downloadFileFromCertificate';

type DetailsProps = {
  certificate: Certificate;
};

const Details = ({ certificate }: DetailsProps) => {
  const { t } = useTranslation();

  const downloadFileMutation = useMutation({
    mutationFn: downloadCertificateFromDws,
    onSuccess: async () => {
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <List sx={{ display: 'flex', flexDirection: 'column' }}>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('CertificateDocRef', { defaultValue: 'Certificate number' })}
            secondary={certificate.docReference}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('CertificateOrgTitle', { defaultValue: 'Organization' })}
            secondary={certificate.title}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('Address', { defaultValue: 'Address' })}
            secondary={certificate.address}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('Standard', { defaultValue: 'Standart' })}
            secondary={certificate.standardReference || '/'}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('CertificateFirstIssued', { defaultValue: 'CertificateFirstIssued' })}
            secondary={
              certificate.firstIssued ? getBeautifiedLocalDate(certificate.firstIssued) : 'N/A'
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('CertificateIssueDate', { defaultValue: 'CertificateIssueDate' })}
            secondary={
              certificate.issueDate ? getBeautifiedLocalDate(certificate.issueDate) : 'N/A'
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('CertificateDocVersion', { defaultValue: 'Certificate version' })}
            secondary={certificate.docVersion}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('CertificateEndDate', { defaultValue: 'CertificateEndDate' })}
            secondary={certificate.endDate ? getBeautifiedLocalDate(certificate.endDate) : 'N/A'}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('Scope', { defaultValue: 'Scope' })}
            secondary={certificate.scope || '/'}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('ScopeNative', { defaultValue: 'ScopeNative' })}
            secondary={certificate.scopeNative || '/'}
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: '1.25rem' }}
            secondaryTypographyProps={{ fontSize: '1rem' }}
            primary={t('ScopeEN', { defaultValue: 'ScopeEN' })}
            secondary={certificate.scopeEn || '/'}
          />
        </ListItem>
      </List>
      {certificate.dwsDok.filename ? (
        <Button
          variant="contained"
          onClick={() => {
            downloadFileMutation.mutate({
              dok: certificate.dwsDok,
              filename: certificate.dwsDok.originalfilename || certificate.title,
            });
          }}
          fullWidth
        >
          {t('Download certificate', { defaultValue: 'Download certificate' })}
        </Button>
      ) : (
        <> </>
      )}
    </Box>
  );
};

export default Details;
